/**
 * Provides a backbone wrapper over the `esri.dijit.Measurement` control.
 * ## Fields
 * Name | Description
 * --- | ---
 * measureTool | The `esri.dijit.Measurement` control active in this view, if any.
 * showUnitSelector | A backbone view acting as the unit selection control.
 * @module app/views/map/MapMeasurementView
 */
import Backbone from "backbone";
import _ from "underscore";
import DropdownButtonView from "core/views/DropdownButtonView";
import messenger from "app/Messenger";
import mapContext from "app/views/map/MapContext";
export default Backbone.View.extend({
  className: "measurement-panel",

  initialize: function (options) {
    this.options = _.defaults({}, options, {});
  },

  template: _.template(
    '<div id="floater"><div id="measureDiv" class="measureDiv"></div></div>'
  ),

  render: function () {
    this.$el.html(this.template(this.options));
  },

  /**
   * Configures this control for a given measurement type.
   * @param  {string} type    The measurement type. One of `"location"`, `"area"`, or `"distance"`.
   */
  configureMeasurement: function (type) {
    var units = window.require("esri/units");
    var Measurement = window.require("esri/dijit/Measurement");
    var dijit = window.require("dijit/dijit");
    var dojo = window.require("dojo");
    var self = this;

    this.render(); //render the template

    try {
      if (dijit.byId("measureTool")) {
        dijit.byId("measureTool").destroyRecursive();
      }

      this.measureTool = new Measurement(
        {
          map: mapContext.getMap(),
          id: "measureTool",
          defaultAreaUnit: units.SQUARE_FEET,
          defaultLengthUnit: units.FEET,
        },
        dojo.create("measurement")
      );
      dojo.place(this.measureTool.domNode, dojo.byId("measureDiv"));
      this.measureTool.startup();
    } catch (err) {
      messenger.error(err);
    }

    this._configureUnitSelector(type);
    this.showUnitSelector.disable();
    this.measureTool.on("measure-end", function () {
      self.showUnitSelector.enable();
    });
  },

  _configureUnitSelector: function (type) {
    var units = window.require("esri/units");
    this.measureTool.clearResult();

    var unitConstants = (this._unitConstants = {
      DMS: "DMS",
      DEGREES: "Degrees",
      ACRES: "Acres",
      SQ_MILES: "Sq Miles",
      SQ_KILOMETERS: "Sq Kilometers",
      HECTARES: "Hectares",
      SQ_YARDS: "Sq Yards",
      SQ_FEET: "Sq Feet",
      SQ_METERS: "Sq Meters",
      MILES: "Miles",
      KILOMETERS: "Kilometers",
      FEET: "Feet",
      METERS: "Meters",
      YARDS: "Yards",
      NAUTICAL_MILES: "Nautical Miles",
    });

    this.measureTool.setTool(type, true);

    switch (type) {
      case "location":
        this.showUnitSelector = new DropdownButtonView({
          text: unitConstants.DEGREES,
          options: [
            {
              text: unitConstants.DMS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.DEGREE_MINUTE_SECONDS,
                  unitConstants.DMS
                );
              }, this),
            },
            {
              text: unitConstants.DEGREES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.DECIMAL_DEGREES,
                  unitConstants.DEGREES
                );
              }, this),
            },
          ],
        });
        break;

      case "area":
        this.showUnitSelector = new DropdownButtonView({
          text: unitConstants.SQ_FEET,
          options: [
            {
              text: unitConstants.ACRES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.ACRES,
                  unitConstants.ACRES
                );
              }, this),
            },
            {
              text: unitConstants.SQ_MILES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.SQUARE_MILES,
                  unitConstants.SQ_MILES
                );
              }, this),
            },
            {
              text: unitConstants.SQ_KILOMETERS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.SQUARE_KILOMETERS,
                  unitConstants.SQ_KILOMETERS
                );
              }, this),
            },
            {
              text: unitConstants.HECTARES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.HECTARES,
                  unitConstants.HECTARES
                );
              }, this),
            },
            {
              text: unitConstants.SQ_YARDS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.SQUARE_YARDS,
                  unitConstants.SQ_YARDS
                );
              }, this),
            },
            {
              text: unitConstants.SQ_FEET,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.SQUARE_FEET,
                  unitConstants.SQ_FEET
                );
              }, this),
            },
            {
              text: unitConstants.SQ_METERS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.SQUARE_METERS,
                  unitConstants.SQ_METERS
                );
              }, this),
            },
          ],
        });
        break;

      case "distance":
        this.showUnitSelector = new DropdownButtonView({
          text: unitConstants.FEET,
          options: [
            {
              text: unitConstants.MILES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.MILES,
                  unitConstants.MILES
                );
              }, this),
            },
            {
              text: unitConstants.KILOMETERS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.KILOMETERS,
                  unitConstants.KILOMETERS
                );
              }, this),
            },
            {
              text: unitConstants.FEET,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.FEET,
                  unitConstants.FEET
                );
              }, this),
            },
            {
              text: unitConstants.METERS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.METERS,
                  unitConstants.METERS
                );
              }, this),
            },
            {
              text: unitConstants.YARDS,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.YARDS,
                  unitConstants.YARDS
                );
              }, this),
            },
            {
              text: unitConstants.NAUTICAL_MILES,
              click: _.bind(function () {
                this._updateUnitsDropdown(
                  this.showUnitSelector,
                  units.NAUTICAL_MILES,
                  unitConstants.NAUTICAL_MILES
                );
              }, this),
            },
          ],
        });
        break;
      default:
        break;
    }

    this.showUnitSelector.render();
    this.showUnitSelector.$(".btn").addClass("measurement-dropdown");
    this.$el.prepend(this.showUnitSelector.$el);
    this.$el.prepend(
      '<label class="control-label measurement-dropdown-label">Select Units</label><br />'
    );
  },

  _updateUnitsDropdown: function (dropDown, type, text) {
    var units = window.require("esri/units");
    dropDown.options.text = text;
    dropDown.render();
    dropDown.$(".dropdown-toggle").addClass("measurement-dropdown");

    if (type == units.DEGREE_MINUTE_SECONDS || type == units.DECIMAL_DEGREES) {
      this.measureTool._switchLocationUnit(type);
    } else {
      this.measureTool._switchUnit(type);
    }
  },
  /**
   * Resets the measurement control
   * @return {[type]} [description]
   */
  resetMeasurement: function () {
    var dijit = window.require("dijit/dijit");

    this.measureTool.setTool("location", false);
    this.measureTool.setTool("area", false);
    this.measureTool.setTool("distance", false);
    this.measureTool.clearResult(); //clear the measurement result from the screen

    try {
      if (dijit.byId("measureTool")) {
        dijit.byId("measureTool").destroyRecursive();
      }
    } catch (err) {}
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/MapMeasurementView.js
// module id = 949
// module chunks = 1